import { type ApolloError, gql, useQuery } from '@apollo/client';

import type {
  GetOnboardingDependantResidencesQuery,
  GetOnboardingDependantResidencesQueryVariables,
} from './__generated__/useOnboardingDependantResidences.gql';
import type { HousingOwnership, ResidenceType } from '../../__generated__/globalTypes';

const RESIDENCE_FIELDS = gql`
  fragment OnboardingResidenceFields on Residence {
    id
    isPrimary
    residenceType
    residenceTypeOther
    addressLine1
    addressLine2
    city
    state
    postalCode
    country
    placeId
    formatted
    ownership
    ownershipOther
    owner {
      __typename
      ... on User {
        id
      }
      ... on Dependant {
        id
      }
    }
  }
`;

const GET_ONBOARDING_DEPENDANT_RESIDENCES = gql`
  query GetOnboardingDependantResidences {
    getCurrentSession {
      id
      user {
        id
        family {
          id
          caregiverRelationships {
            id
            caregiver {
              id
            }
            dependant {
              id
              primaryResidence {
                ...OnboardingResidenceFields
              }
              otherResidences {
                ...OnboardingResidenceFields
              }
            }
          }
        }
      }
    }
  }
  ${RESIDENCE_FIELDS}
`;

/**
 * Projected residence row consumed by the residence onboarding step. Owner
 * is flattened from the `FamilyMember` union into a typed discriminator —
 * the step only needs to ask "is this owned by user X, dependant Y, or
 * family-shared?" and a flat shape makes that a one-line check.
 */
export interface IOnboardingResidence {
  id: string;
  isPrimary: boolean;
  residenceType: ResidenceType;
  residenceTypeOther?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city: string;
  state?: string | null;
  postalCode?: string | null;
  country: string;
  placeId?: string | null;
  formatted?: string | null;
  ownership?: HousingOwnership | null;
  ownershipOther?: string | null;
  /** Owner kind: `'user'` if owned by a caregiver, `'dependant'` if owned by a dependant, `null` for family-shared. */
  ownerKind: 'user' | 'dependant' | null;
  ownerId: string | null;
}

export interface IDependantResidences {
  dependantId: string;
  /** The single primary residence the dependant lives at — directly owned, family-shared, or via a cohabitant. `null` when none is captured yet. */
  primary: IOnboardingResidence | null;
  /** Every other residence the dependant has access to (other primary residences from cohabitants, plus shared frequent-location rows). */
  other: IOnboardingResidence[];
}

export interface IUseOnboardingDependantResidencesResult {
  /**
   * One entry per dependant the user owns (caregiver === current user), in
   * the API's caregiver-relationship order. The same `Residence` may appear
   * under multiple dependants (e.g. partners living together share a primary;
   * a family-shared frequent location appears under both) — consumers should
   * dedupe by id when they care about uniqueness.
   */
  dependantResidences: IDependantResidences[];
  loading: boolean;
  error: ApolloError | undefined;
  /**
   * Re-runs the query against the network. Call after mutating residences so
   * the server-derived `primaryResidence` / `otherResidences` fields refresh
   * in the cache — a normalized-cache merge alone can't relink them, so a
   * later remount (e.g. wizard back-nav) would otherwise read stale values.
   */
  refetch: () => Promise<unknown>;
}

interface IGqlResidence {
  __typename?: 'Residence';
  id: string;
  isPrimary: boolean;
  residenceType: ResidenceType;
  residenceTypeOther?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city: string;
  state?: string | null;
  postalCode?: string | null;
  country: string;
  placeId?: string | null;
  formatted?: string | null;
  ownership?: HousingOwnership | null;
  ownershipOther?: string | null;
  owner?: { __typename: 'User' | 'Dependant'; id: string } | null;
}

const projectResidence = (r: IGqlResidence): IOnboardingResidence => {
  let ownerKind: IOnboardingResidence['ownerKind'] = null;
  let ownerId: string | null = null;
  if (r.owner?.__typename === 'User') {
    ownerKind = 'user';
    ownerId = r.owner.id;
  } else if (r.owner?.__typename === 'Dependant') {
    ownerKind = 'dependant';
    ownerId = r.owner.id;
  }
  return {
    id: r.id,
    isPrimary: r.isPrimary,
    residenceType: r.residenceType,
    residenceTypeOther: r.residenceTypeOther,
    addressLine1: r.addressLine1,
    addressLine2: r.addressLine2,
    city: r.city,
    state: r.state,
    postalCode: r.postalCode,
    country: r.country,
    placeId: r.placeId,
    formatted: r.formatted,
    ownership: r.ownership,
    ownershipOther: r.ownershipOther,
    ownerKind,
    ownerId,
  };
};

/**
 * Reads each user-owned dependant's reachable residences via
 * `Dependant.primaryResidence` + `Dependant.otherResidences`. The server
 * does cohabitant resolution (partner / caregiver lives-with chains), so
 * the client receives a per-dependant view without re-doing that math
 * locally.
 *
 * Filters caregiver relationships to the current user — same scoping rule
 * as `useOnboardingDependants` — so a co-caregiver's dependants don't leak
 * into the result.
 */
export const useOnboardingDependantResidences = (): IUseOnboardingDependantResidencesResult => {
  const { data, loading, error, refetch } = useQuery<
    GetOnboardingDependantResidencesQuery,
    GetOnboardingDependantResidencesQueryVariables
  >(GET_ONBOARDING_DEPENDANT_RESIDENCES, { fetchPolicy: 'cache-and-network' });

  const user = data?.getCurrentSession?.user ?? null;
  if (user === null) {
    return { dependantResidences: [], loading, error, refetch };
  }

  const dependantResidences: IDependantResidences[] = user.family.caregiverRelationships
    .filter((r) => r.caregiver.id === user.id)
    .map((r) => ({
      dependantId: r.dependant.id,
      primary: r.dependant.primaryResidence ? projectResidence(r.dependant.primaryResidence) : null,
      other: r.dependant.otherResidences.map(projectResidence),
    }));

  return { dependantResidences, loading, error, refetch };
};
